// SEQ-226: variation grouping shared by the Variations tab list and the
// "View Source Variation" button on the Install & Hire register.
//
// The list drops hire-only variations and applies the tab filters. The button
// must not: it looks the source variation up in the full project list.
import type { Database } from "../types/database.types";
import type { ReconciliationStatus } from "./reconciliationStatus";

export type Variation = Database["public"]["Tables"]["claims_variations"]["Row"];

export interface GroupedVariation {
  id: string;
  docket_no: string;
  description?: string;
  reconciliation_status?: ReconciliationStatus;
  variation_date: string;
  items: Variation[];
  totalApproved: number;
  totalWaiting: number;
  totalAmount: number;
  status: string;
  pdf_url?: string;
  audit_flag?: string;
  comment_count?: number;
}

export const getVariationHireItems = (variation: Variation): unknown[] => {
  const hireItems = variation.hire_items;
  if (Array.isArray(hireItems)) return hireItems;
  if (typeof hireItems === "string") {
    try {
      const parsed = JSON.parse(hireItems);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

export const hasVariationHireItems = (variation: Variation): boolean =>
  getVariationHireItems(variation).length > 0 ||
  Number(variation.hire_items_total || 0) > 0;

export const getVariationWorksRawTotal = (variation: Variation): number => {
  const rawTotal = Number(variation.total || 0);
  if (!hasVariationHireItems(variation)) return rawTotal;

  const hireTotal = Number(variation.hire_items_total || 0);
  if (hireTotal <= 0) return rawTotal;

  return Math.max(0, rawTotal - hireTotal);
};

export const isHireOnlyVariation = (variation: Variation): boolean =>
  hasVariationHireItems(variation) &&
  Math.abs(getVariationWorksRawTotal(variation)) <= 0.01;

/**
 * Group variation rows by docket and total them. Every row passed in is
 * grouped: callers decide what to leave out (the list leaves out hire-only
 * rows and applies its filters before calling this).
 */
export function buildVariationGroups(rows: Variation[]): GroupedVariation[] {
  const groups = new Map<string, GroupedVariation>();
  rows.forEach((v) => {
    const docket = v.docket_no || "Unspecified";
    if (!groups.has(docket)) {
      groups.set(docket, {
        id: docket,
        docket_no: docket,
        description: v.title || v.notes || undefined,
        variation_date: v.variation_date,
        totalAmount: 0,
        totalApproved: 0,
        totalWaiting: 0,
        status: v.status || "pending",
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
        pdf_url: (v as any).pdf_url,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
        audit_flag: (v as any).audit_flag || "green",
        comment_count: 0,
        items: [],
      });
    }
    const group = groups.get(docket)!;
    group.items.push(v);
    // Aggregate reconciliation_status: group status = first item's status (they share the same docket)
    if (group.items.length === 1) {
      group.reconciliation_status = (v.reconciliation_status as ReconciliationStatus) || null;
    } else {
      // If ALL items share the same status, use that; otherwise null (mixed)
      const firstStatus = group.items[0]?.reconciliation_status || null;
      const allSame = group.items.every(item => (item.reconciliation_status || null) === firstStatus);
      group.reconciliation_status = allSame ? (firstStatus as ReconciliationStatus) : null;
    }

    const isCredit = v.debit_credit === "credit";
    const multiplier = isCredit ? -1 : 1;

    const rawTotal = getVariationWorksRawTotal(v);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
    const discount = Number((v as any).discount_percent) || 0;
    const effectiveTotal = rawTotal * (1 - discount / 100);

    const total = effectiveTotal * multiplier;
    const approvedRaw = Number(v.approved_amount) || 0;
    const approved = (
      hasVariationHireItems(v) ? Math.min(approvedRaw, rawTotal) : approvedRaw
    ) * multiplier;

    group.totalAmount += total;
    group.totalApproved += approved;

    // Recalculate waiting: Total of Group - Approved of Group
    group.totalWaiting = group.totalAmount - group.totalApproved;
  });

  // Sort by date desc (using the date of the first item effectively, which is usually same for docket)
  return Array.from(groups.values()).sort(
    (a, b) =>
      new Date(b.variation_date).getTime() -
      new Date(a.variation_date).getTime(),
  );
}

/**
 * Find the group a variation id belongs to, for "View Source Variation".
 * Searches the full, unfiltered list, so hire-only variations and variations
 * hidden by the Variations tab filters are found. The group holds the
 * variation itself plus the works rows that share its docket (what the list
 * modal shows for that docket). Returns null when the id is not in the list.
 */
export function findSourceVariationGroup(
  variations: Variation[] | null | undefined,
  variationId: string | null | undefined,
): GroupedVariation | null {
  if (!variations || !variationId) return null;
  const source = variations.find((v) => v.id === variationId);
  if (!source) return null;
  const docket = source.docket_no || "Unspecified";
  const rows = variations.filter(
    (v) =>
      v.id === source.id ||
      ((v.docket_no || "Unspecified") === docket && !isHireOnlyVariation(v)),
  );
  return buildVariationGroups(rows).find((g) => g.id === docket) ?? null;
}

const LABOUR_HOUR_FIELDS = [
  "hours_lvl_1",
  "hours_lvl_2",
  "saturday_work_qty",
  "sat_contract_ot_qty",
  "sat_contract_normal_qty",
] as const;

// SEQ-226: true when the Day Labour section should show its empty state.
// Only a group made entirely of hire-only variations with no day labour rows
// and no labour hours qualifies. Every other group keeps the existing
// fallback row, including legacy ones whose labour lives in the hours fields.
export function hasNoDayLabour(
  items: Variation[],
  dayLabourRows: unknown[],
): boolean {
  if (dayLabourRows.length > 0) return false;
  if (items.length === 0) return false;
  return items.every(
    (item) =>
      isHireOnlyVariation(item) &&
      LABOUR_HOUR_FIELDS.every(
        (f) => !Number((item as unknown as Record<string, unknown>)[f] || 0),
      ),
  );
}
